import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { catalogById } from '../data/catalog';
import { BEGINNER_CONTENT_BY_COURSE } from '../data/courseBeginnerContent';
import CourseLevelPicker from '../components/CourseLevelPicker';
import BeginnerCourseContent from '../components/BeginnerCourseContent';
import WaitlistSection from '../components/WaitlistSection';
import usePageTitle from '../hooks/usePageTitle';

const CourseDetail = () => {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBeginner, setShowBeginner] = useState(false);
  const beginnerContent = BEGINNER_CONTENT_BY_COURSE[courseId];
  const isSingleLevel = course?.single_level;

  usePageTitle(course?.title || 'Course');

  useEffect(() => {
    const nextCourse = catalogById[courseId] || null;
    setCourse(nextCourse);
    setShowBeginner(Boolean(nextCourse?.single_level));
    setLoading(false);
  }, [courseId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="animate-spin rounded-full h-10 w-10 border-t border-b border-[#A80D1A]" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <h2 className="font-serif text-3xl text-white mb-4">Course not found</h2>
          <Link to="/courses" className="text-[#C8102E] hover:underline text-sm tracking-[0.2em] uppercase">
            ← Back to courses
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black film-grain pb-24">
      <section className="relative pt-32 pb-20 border-b border-white/5 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src={course.thumbnail} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/40" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12">
          <Link
            to="/courses"
            className="text-xs tracking-[0.2em] uppercase text-zinc-500 hover:text-white transition-colors mb-8 inline-block"
          >
            ← Back to Courses
          </Link>

          <div className="flex items-center gap-3 mb-6">
            <span className="chapter-label">{course.category?.toUpperCase() || 'COURSE'}</span>
          </div>

          <h1 data-testid="course-detail-title" className="display-heading text-5xl sm:text-6xl lg:text-7xl text-white mb-4 leading-none">
            {course.title}
          </h1>
          <p className="font-serif italic text-xl md:text-2xl text-zinc-400 mb-8">&ldquo;{course.tagline}&rdquo;</p>
          <p className="text-base leading-relaxed text-zinc-300 max-w-2xl whitespace-pre-line">
            {course.long_description || course.description}
          </p>
        </div>
      </section>

      <section className="py-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {isSingleLevel || showBeginner ? (
            <>
              {!isSingleLevel && (
                <button
                  type="button"
                  onClick={() => setShowBeginner(false)}
                  className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-zinc-500 hover:text-white transition-colors mb-10"
                  data-testid="course-level-back"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to levels
                </button>
              )}
              <BeginnerCourseContent
                content={beginnerContent}
                instructor={course.instructor}
                singleLevel={isSingleLevel}
                facultyImage={courseId === 'cyn_direction' ? '/abhay.jpeg' : undefined}
                videoDuration={course.total_duration}
              />
            </>
          ) : (
            <>
              <p className="chapter-label mb-6">CHOOSE YOUR LEVEL</p>
              <h2 className="display-heading text-4xl md:text-5xl text-white mb-4">Start where you are.</h2>
              <p className="text-base text-zinc-400 mb-12 max-w-2xl">
                Each level builds on the last - pick the track that matches your experience.
              </p>
              <CourseLevelPicker
                courseThumbnail={course.thumbnail}
                beginnerPrice={course.beginner_price}
                onSelectBeginner={() => setShowBeginner(true)}
              />
            </>
          )}
        </div>
      </section>

      {(isSingleLevel || showBeginner) && (
        <>
          <div className="film-strip w-full"></div>

          <WaitlistSection
            chapterLabel="JOIN THE WAITLIST"
            defaultCourseInterest={course.title}
            className="py-24 md:py-32 bg-black border-t border-white/5"
          />
        </>
      )}
    </div>
  );
};

export default CourseDetail;
